import { useEffect, useMemo, useState, type JSX } from "react";

import { type Availability } from "./atlas/datasource";
import {
  type Assessment,
  type AssessmentEvidence,
  type AssessmentFinding,
  type Severity,
  evidenceForFinding,
  rankAssessmentFindings,
  recommendationForFinding,
  summarizeAssessment,
} from "./atlas/assessment-source";
import { DATASETS, DEFAULT_DATASET_ID, datasetRef, loadAssessment, loadDiscovery } from "./atlas/dataset-registry";
import {
  projectSnapshot,
  deriveDiscoverySummary,
  deriveDiscoveryIntelligence,
  deriveDiscoveryExecutive,
  type DiscoverySnapshot,
  type DiscoveryIntelligence,
  type DiscoveryEvidence,
} from "./atlas/snapshot-source";

const TABS = ["Today", "Environment", "Changes", "Relationships", "Findings", "Investigate", "Assessments", "Executive"] as const;
type Tab = (typeof TABS)[number];
const SEVERITIES: readonly Severity[] = ["critical", "high", "medium", "low", "informational"];

const fmt = (ts: string | null | undefined): string => (ts ? ts.replace("T", " ").slice(0, 16) + "Z" : "—");

function Unavailable({ title, reason, missing }: { title: string; reason: string; missing: string }): JSX.Element {
  return (
    <div className="cc-unavailable" role="status">
      <div className="cc-unavailable__badge">Not derived</div>
      <h3>{title}</h3>
      <p>{reason}</p>
      <p className="cc-unavailable__missing">Engine capability required: {missing}</p>
    </div>
  );
}
function SevPill({ s }: { s: Severity }): JSX.Element { return <span className={`cc-sev cc-sev--${s}`}>{s}</span>; }
function VerifPill({ v }: { v: string }): JSX.Element { return <span className={`cc-verif cc-verif--${v}`}>{v}</span>; }
function Metric({ label, value, sub }: { label: string; value: string | number; sub?: string }): JSX.Element {
  return <div className="cc-metric"><div className="cc-metric__value">{value}</div><div className="cc-metric__label">{label}</div>{sub ? <div className="cc-metric__sub">{sub}</div> : null}</div>;
}
const avail = (a: Availability<number>): string => (a.available ? String(a.value) : "—");

/** First/last observed for a finding, derived from its evidence timestamps (honest: undefined when the
 *  finding carries no dated evidence — never invented). */
function observedWindow(a: Assessment, f: AssessmentFinding): { first?: string; last?: string } {
  const ids = new Set(f.evidence.map((e) => e.evidenceId));
  const times = a.evidence.filter((e) => ids.has(e.evidenceId)).map((e) => e.collectedAt).filter(Boolean).sort();
  return { first: times[0], last: times[times.length - 1] };
}

/**
 * Intelligence card for a real finding. Only renders an action whose underlying data exists:
 * Investigate (always — a finding is a conclusion Atlas reached), View Evidence (iff evidence),
 * Relationships (iff the finding recorded relationships), Replay Change (iff a real change exists).
 */
function FindingCard({ a, f, onInvestigate, onEvidence, onRelationships }: {
  a: Assessment;
  f: AssessmentFinding;
  onInvestigate: (id: string) => void;
  onEvidence: (f: AssessmentFinding) => void;
  onRelationships: (id: string) => void;
}): JSX.Element {
  const w = observedWindow(a, f);
  const hasRels = f.relationships.length > 0;
  return (
    <article className="cc-card">
      <div className="cc-card__row"><SevPill s={f.severity} /><span className="cc-card__cat">{f.category}</span></div>
      <h4>{f.title}</h4>
      <p className="cc-card__summary">{f.summary}</p>
      <div className="cc-card__meta">{f.assets.length} affected · {f.evidence.length} evidence · <VerifPill v={f.verification} /></div>
      {(w.first || w.last) && <div className="cc-card__obs">first observed {fmt(w.first)} · last {fmt(w.last)}</div>}
      <div className="cc-card__actions">
        <button className="cc-btn" onClick={() => onInvestigate(f.id)}>Investigate</button>
        {f.evidence.length > 0 && <button className="cc-btn cc-btn--ghost" onClick={() => onEvidence(f)}>View Evidence</button>}
        {hasRels && <button className="cc-btn cc-btn--ghost" onClick={() => onRelationships(f.id)}>Relationships</button>}
        {/* Replay Change intentionally absent: no real change/history exists for this finding. */}
      </div>
    </article>
  );
}

/**
 * Intelligence card for a discovery observation — a PROJECTION of the engine's own posture/risk/operational
 * item (never a new conclusion). Actions render only when supported: Investigate + Relationships always
 * (org-scoped context exists), View Evidence when provenance exists. History/Replay are NEVER offered — a
 * discovery brief is a single observation with no prior snapshot to diff.
 */
function DiscoveryIntelCard({ c, onInvestigate, onEvidence, onRelationships }: {
  c: DiscoveryIntelligence;
  onInvestigate: (id: string) => void;
  onEvidence: (c: DiscoveryIntelligence) => void;
  onRelationships: () => void;
}): JSX.Element {
  return (
    <article className={`cc-card ${c.attention ? "cc-card--attention" : ""}`}>
      <div className="cc-card__row"><span className={`cc-chip cc-chip--${c.attention ? "attention" : c.kind}`}>{c.attention ? "Attention" : c.kind}</span></div>
      <h4>{c.title}</h4>
      <p className="cc-card__summary">{c.detail}</p>
      <div className="cc-card__meta">basis: {c.basis}</div>
      <div className="cc-card__actions">
        <button className="cc-btn" onClick={() => onInvestigate(c.id)}>Investigate</button>
        {c.evidence.length > 0 && <button className="cc-btn cc-btn--ghost" onClick={() => onEvidence(c)}>View Evidence</button>}
        {c.relatedEntityIds.length > 0 && <button className="cc-btn cc-btn--ghost" onClick={onRelationships}>Relationships</button>}
        {/* History intentionally absent: single observation, no prior snapshot to diff (c.historyAvailable === false). */}
      </div>
    </article>
  );
}

function RunDiscoveryEmpty(): JSX.Element {
  return (
    <section className="cc-empty">
      <div className="cc-empty__mark">◆</div>
      <h2>Atlas has not observed an environment yet</h2>
      <p>There is no assessment run or discovery observation selected. When Atlas has observed and retained an environment, this becomes your intelligence overview — assets, findings, evidence, relationships and change, each traceable to why.</p>
      <p className="cc-muted">The legitimate next action supported by the current system is to run discovery against a target, or select an existing observed environment above.</p>
      <div className="cc-empty__actions">
        <a className="cc-btn" href="/discovery/" title="Atlas Discovery">Run Discovery</a>
      </div>
      <p className="cc-empty__note">Nothing here is fabricated. No sample findings, no placeholder infrastructure — only what Atlas has genuinely observed.</p>
    </section>
  );
}

function AssessmentEvidenceDrawer({ title, evidence, onClose }: { title: string; evidence: readonly AssessmentEvidence[]; onClose: () => void }): JSX.Element {
  return (
    <aside className="cc-drawer" aria-label="Evidence">
      <header className="cc-drawer__head"><div><div className="cc-eyebrow">Evidence</div><h2>{title}</h2></div><button className="cc-x" onClick={onClose} aria-label="Close evidence">×</button></header>
      {evidence.length === 0 ? <p className="cc-muted">No evidence records for this subject.</p> : (
        <ol className="cc-evlist">
          {evidence.map((e) => (
            <li key={e.evidenceId} className="cc-ev">
              <div className="cc-ev__top"><code>{e.evidenceId.slice(0, 18)}</code><VerifPill v={e.verification} /></div>
              <div className="cc-ev__chain"><span>{e.source}</span><span>→</span><span>{e.collectorId}</span><span>→</span><span>{e.kind}</span><span>→</span><span>{fmt(e.collectedAt)}</span></div>
              <div className="cc-ev__conf">confidence {(e.confidence * 100).toFixed(0)}% · {e.observationKey}</div>
              {e.excerpt ? <pre className="cc-ev__obs">{e.excerpt.slice(0, 600)}</pre> : null}
              {e.hash ? <div className="cc-ev__conf">hash {e.hash.slice(0, 24)}…</div> : null}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

/** Evidence drawer for a discovery observation — makes "why Atlas is showing this" visible: the public
 *  observations (with their collector basis + timestamp) that back the card. Real provenance, not marketing. */
function DiscoveryEvidenceDrawer({ title, evidence, onClose }: { title: string; evidence: readonly DiscoveryEvidence[]; onClose: () => void }): JSX.Element {
  return (
    <aside className="cc-drawer" aria-label="Evidence">
      <header className="cc-drawer__head"><div><div className="cc-eyebrow">Evidence</div><h2>{title}</h2></div><button className="cc-x" onClick={onClose} aria-label="Close evidence">×</button></header>
      <p className="cc-muted">Why Atlas is showing this — the public observations behind it. Public DNS/TLS/HTTP only; no credentials, nothing on the target accessed.</p>
      {evidence.length === 0 ? <p className="cc-muted">No provenance recorded for this observation.</p> : (
        <ol className="cc-evlist">
          {evidence.map((e, i) => (
            <li key={i} className="cc-ev">
              <div className="cc-ev__top"><code>{e.source}</code><VerifPill v={e.verification} /></div>
              <div className="cc-ev__chain"><span>collector:discovery</span><span>→</span><span>{e.source}</span><span>→</span><span>{fmt(e.observedAt)}</span></div>
              <pre className="cc-ev__obs">{e.observation}</pre>
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

type DrawerState =
  | { kind: "assessment"; title: string; evidence: readonly AssessmentEvidence[] }
  | { kind: "discovery"; title: string; evidence: readonly DiscoveryEvidence[] };

/** Persist the selected environment across reloads — validated against the real registry so a stale id
 *  never resurrects a removed dataset. "" (No environment) is a legitimate saved choice. */
const DATASET_STORAGE_KEY = "atlas.cc.datasetId";
function initialDatasetId(): string {
  try {
    const saved = window.localStorage.getItem(DATASET_STORAGE_KEY);
    if (saved !== null && (saved === "" || DATASETS.some((d) => d.id === saved))) return saved;
  } catch { /* no storage (SSR/tests) — fall through to default */ }
  return DEFAULT_DATASET_ID;
}

export function App(): JSX.Element {
  const [datasetId, setDatasetId] = useState<string>(initialDatasetId);
  const [tab, setTab] = useState<Tab>("Today");
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [selectedIntelId, setSelectedIntelId] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  const ref = datasetRef(datasetId);
  const assessment: Assessment | null = ref?.kind === "assessment" ? loadAssessment(datasetId) : null;

  // Discovery snapshots are code-split and loaded on selection (async). `discovery` is null while loading.
  const [discovery, setDiscovery] = useState<DiscoverySnapshot | null>(null);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  useEffect(() => {
    if (ref?.kind !== "discovery") { setDiscovery(null); setDiscoveryLoading(false); return; }
    let alive = true;
    setDiscovery(null);
    setDiscoveryLoading(true);
    void loadDiscovery(datasetId).then((s) => { if (alive) { setDiscovery(s); setDiscoveryLoading(false); } });
    return () => { alive = false; };
  }, [datasetId, ref?.kind]);

  const summary = useMemo(() => (assessment ? summarizeAssessment(assessment) : null), [assessment]);
  const findings = useMemo(() => (assessment ? rankAssessmentFindings(assessment) : []), [assessment]);
  const selectedFinding: AssessmentFinding | null = selectedFindingId && assessment ? assessment.findings.find((f) => f.id === selectedFindingId) ?? null : null;

  const discoverySummary = useMemo(() => (discovery ? deriveDiscoverySummary(discovery) : null), [discovery]);
  const discoveryIntel = useMemo(() => (discovery ? deriveDiscoveryIntelligence(discovery) : []), [discovery]);
  const discoveryExec = useMemo(() => (discovery ? deriveDiscoveryExecutive(discovery) : null), [discovery]);
  const discoveryProjection = useMemo(() => (discovery ? projectSnapshot(discovery) : null), [discovery]);
  const rankedIntel = useMemo(() => [...discoveryIntel].sort((a, b) => Number(b.attention) - Number(a.attention)), [discoveryIntel]);
  const selectedIntel = selectedIntelId ? discoveryIntel.find((c) => c.id === selectedIntelId) ?? null : null;

  const investigate = (id: string): void => { setSelectedFindingId(id); setTab("Investigate"); };
  const relFromFinding = (id: string): void => { setSelectedFindingId(id); setTab("Relationships"); };
  const openFindingEvidence = (f: AssessmentFinding): void => setDrawer({ kind: "assessment", title: f.title, evidence: assessment ? evidenceForFinding(assessment, f.id) : [] });
  const investigateIntel = (id: string): void => { setSelectedIntelId(id); setTab("Investigate"); };
  const openIntelEvidence = (c: DiscoveryIntelligence): void => setDrawer({ kind: "discovery", title: c.title, evidence: c.evidence });

  const onSelectDataset = (id: string): void => {
    setDatasetId(id);
    setSelectedFindingId(null);
    setSelectedIntelId(null);
    try { window.localStorage.setItem(DATASET_STORAGE_KEY, id); } catch { /* no storage — selection is still applied in-session */ }
  };

  const assetsByKind = useMemo(() => {
    const m = new Map<string, Set<string>>();
    if (assessment) for (const f of assessment.findings) for (const a of f.assets) { const set = m.get(a.kind) ?? new Set(); set.add(a.name); m.set(a.kind, set); }
    return m;
  }, [assessment]);

  const assessmentDatasets = DATASETS.filter((d) => d.kind === "assessment");
  const discoveryDatasets = DATASETS.filter((d) => d.kind === "discovery");

  return (
    <div className="cc">
      <nav className="cc-rail" aria-label="Atlas intelligence">
        <div className="cc-brand"><span className="cc-brand__mark">◆</span> Atlas</div>
        {TABS.map((t) => <button key={t} className={`cc-tab ${t === tab ? "is-active" : ""}`} onClick={() => setTab(t)} aria-current={t === tab}>{t}</button>)}
        <div className="cc-rail__foot">Command Center</div>
      </nav>

      <main className="cc-main">
        <header className="cc-top">
          <div><div className="cc-eyebrow">Environment Intelligence</div><h1>{tab}</h1></div>
          <label className="cc-dataset">
            <span className="cc-dataset__label">Environment</span>
            <select value={datasetId} onChange={(e) => onSelectDataset(e.target.value)}>
              <option value="">— No environment —</option>
              <optgroup label={`Assessments (${assessmentDatasets.length})`}>
                {assessmentDatasets.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </optgroup>
              <optgroup label={`Discovery observations (${discoveryDatasets.length})`}>
                {discoveryDatasets.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </optgroup>
            </select>
          </label>
        </header>
        {ref && <div className="cc-prov" title="Provenance">{ref.provenance}</div>}

        {!ref && <RunDiscoveryEmpty />}

        {/* ---- Assessment-backed experience (canonical rich path) ---- */}
        {assessment && summary && (
          <>
            {tab === "Today" && (
              <section className="cc-view">
                <div className="cc-metrics">
                  <Metric label="Assets observed" value={avail(summary.assetsObserved)} sub={summary.assetsObserved.available ? undefined : "not in artifact"} />
                  <Metric label="Evidence records" value={summary.evidenceRecords} />
                  <Metric label="Active findings" value={summary.activeFindings} />
                  <Metric label="Verified findings" value={summary.verifiedFindings} sub={summary.verifiedFindings === 0 ? "none independently verified" : undefined} />
                  <Metric label="Controls assessed" value={summary.controlsAssessed} />
                  <Metric label="Changes since previous" value={avail(summary.changesSincePrevious)} sub={summary.changesSincePrevious.available ? undefined : "no prior observation"} />
                </div>
                <p className="cc-lastobs">Current assessment: {summary.environmentLabel} · Last observation {fmt(summary.generatedAt)} · {summary.certified ? "certificate present" : "uncertified"}</p>
                <h3 className="cc-h3">Findings by severity</h3>
                <div className="cc-sevbar">{SEVERITIES.map((s) => summary.findingsBySeverity[s] > 0 ? <div key={s} className={`cc-sevseg cc-sevseg--${s}`}>{s} {summary.findingsBySeverity[s]}</div> : null)}</div>
                <h3 className="cc-h3">What needs attention</h3>
                <div className="cc-cards">
                  {findings.slice(0, 6).map((f) => <FindingCard key={f.id} a={assessment} f={f} onInvestigate={investigate} onEvidence={openFindingEvidence} onRelationships={relFromFinding} />)}
                </div>
              </section>
            )}

            {tab === "Findings" && (
              <section className="cc-view">
                <div className="cc-cards">
                  {findings.map((f) => <FindingCard key={f.id} a={assessment} f={f} onInvestigate={investigate} onEvidence={openFindingEvidence} onRelationships={relFromFinding} />)}
                </div>
              </section>
            )}

            {tab === "Investigate" && (
              <section className="cc-view">
                {!selectedFinding ? <p className="cc-muted">Select a finding (from Today or Findings) to investigate.</p> : (
                  <div className="cc-investigate">
                    <div className="cc-card__row"><SevPill s={selectedFinding.severity} /><span className="cc-card__cat">{selectedFinding.category}</span></div>
                    <h3 className="cc-h3">{selectedFinding.title}</h3>
                    <p className="cc-lead">{selectedFinding.summary}</p>
                    <div className="cc-two">
                      <div>
                        <h4 className="cc-h4">Affected assets</h4>
                        <ul className="cc-edges">{selectedFinding.assets.map((a) => <li key={a.entityId} className="cc-edge"><span className="cc-edge__type">{a.kind}</span> {a.name}</li>)}{selectedFinding.assets.length === 0 && <li className="cc-muted">None recorded.</li>}</ul>
                        <h4 className="cc-h4">Recommendation</h4>
                        {(() => { const r = assessment ? recommendationForFinding(assessment, selectedFinding.id) : undefined; return r ? <p className="cc-muted">[{r.priority}] {r.action} — {r.rationale}</p> : <p className="cc-muted">No recommendation recorded.</p>; })()}
                      </div>
                      <div>
                        <h4 className="cc-h4">Evidence</h4>
                        <button className="cc-btn cc-btn--ghost" onClick={() => openFindingEvidence(selectedFinding)}>View evidence chain ({selectedFinding.evidence.length})</button>
                        <h4 className="cc-h4" style={{ marginTop: 16 }}>Provenance</h4>
                        {assessment?.certificate ? <p className="cc-muted">Certified · assessment {assessment.certificate.assessmentId.slice(0, 18)}… · findingsHash {assessment.certificate.findingsHash.slice(0, 16)}…</p> : <p className="cc-muted">No certificate.</p>}
                      </div>
                    </div>
                    <Unavailable title="Replay · impact analysis" reason="Timeline replay and blast-radius impact require change history; this engagement has no prior observation retained." missing="@atlas/memory history + @atlas/impact over ≥2 runs" />
                  </div>
                )}
              </section>
            )}

            {tab === "Environment" && (
              <section className="cc-view cc-env">
                {assetsByKind.size === 0 ? <p className="cc-muted">No assets referenced by findings in this engagement.</p> : [...assetsByKind.entries()].map(([kind, names]) => (
                  <div key={kind} className="cc-env__group"><h3 className="cc-h3">{kind} <span className="cc-count">{names.size}</span></h3>
                    <div className="cc-rows">{[...names].map((n) => <div key={n} className="cc-row"><span className="cc-row__name">{n}</span></div>)}</div>
                  </div>
                ))}
                <p className="cc-muted" style={{ marginTop: 12 }}>Assets shown are those referenced by findings. A full inventory view binds to the assessment inventory control in a later slice.</p>
              </section>
            )}

            {tab === "Changes" && <section className="cc-view"><Unavailable title="No change history for this engagement" reason="This assessment retains no prior observation (history is empty), so before → change → after cannot be derived. This is 'no baseline', not '0 changes'." missing="@atlas/memory history/diff over ≥2 persisted runs" /></section>}

            {tab === "Relationships" && (
              <section className="cc-view">
                {/* Finding→Relationships context: when a finding is selected, focus on ITS relationships. */}
                {selectedFinding && <p className="cc-lead">Focused on finding: <strong>{selectedFinding.title}</strong></p>}
                {(() => {
                  const rels = selectedFinding ? selectedFinding.relationships : assessment.findings.flatMap((f) => f.relationships);
                  return rels.length > 0
                    ? <ul className="cc-edges">{rels.map((r, i) => <li key={i} className="cc-edge"><span className="cc-edge__type">{r.type ?? "related"}</span> {r.from} → {r.to}</li>)}</ul>
                    : <Unavailable title={selectedFinding ? "No relationships recorded for this finding" : "No relationships observed in this engagement"} reason="On-host acceptance findings did not record cross-entity relationships. This is 'not observed/retained', not 'no relationships exist'." missing="@atlas/graph observed edges over this environment" />;
                })()}
              </section>
            )}

            {tab === "Assessments" && (
              <section className="cc-view">
                <h3 className="cc-h3">Scope</h3>
                <div className="cc-kv"><div><span>target</span><code>{assessment.scope.target}</code></div><div><span>roots</span><code>{assessment.scope.roots.join(", ")}</code></div></div>
                <h4 className="cc-h4">Collectors run</h4>
                <ul className="cc-edges">{assessment.scope.collectorsRun.map((c) => <li key={c.id} className="cc-edge"><span className="cc-edge__type">{c.status}</span> {c.id} · {c.observationCount} obs</li>)}</ul>
                <h3 className="cc-h3">Controls ({assessment.controls.length})</h3>
                <ul className="cc-edges">{assessment.controls.map((c) => <li key={c.id} className="cc-edge"><span className="cc-edge__type">{c.status}</span> {c.area}: {c.title}</li>)}</ul>
                {assessment.certificate && <><h3 className="cc-h3">Certificate</h3><div className="cc-kv"><div><span>assessmentId</span><code>{assessment.certificate.assessmentId}</code></div><div><span>generatedAt</span><code>{fmt(assessment.certificate.generatedAt)}</code></div><div><span>selfHash</span><code>{assessment.certificate.selfHash.slice(0, 40)}…</code></div></div></>}
              </section>
            )}

            {tab === "Executive" && (
              <section className="cc-view">
                {assessment.executiveSummary ? (
                  <div className="cc-exec-full">
                    {assessment.executiveSummary.headline && <h3 className="cc-h3">{assessment.executiveSummary.headline}</h3>}
                    {assessment.executiveSummary.posture && <p className="cc-lead">Posture: {assessment.executiveSummary.posture}</p>}
                    {assessment.executiveSummary.narrative && <p className="cc-muted">{assessment.executiveSummary.narrative}</p>}
                    {assessment.executiveSummary.coverageNote && <p className="cc-muted"><em>{assessment.executiveSummary.coverageNote}</em></p>}
                    <div className="cc-exec"><div><h4 className="cc-h4">What changed</h4><Unavailable title="Not derived" reason="No prior observation retained." missing="@atlas/memory history" /></div><div><h4 className="cc-h4">Risk / impact</h4><p className="cc-muted">Risk scores are computed per finding by the engine; no fabricated business-impact figures are shown.</p></div></div>
                  </div>
                ) : <Unavailable title="No executive summary in this artifact" reason="This engagement did not emit an executive summary." missing="assessment executiveSummary" />}
              </section>
            )}
          </>
        )}

        {/* ---- Discovery-backed experience (real external-domain observation) ---- */}
        {ref?.kind === "discovery" && !discovery && (
          <section className="cc-view"><p className="cc-muted">{discoveryLoading ? "Loading observation…" : "This observation could not be loaded."}</p></section>
        )}

        {ref?.kind === "discovery" && discovery && discoverySummary && discoveryExec && discoveryProjection && (
          <section className="cc-view">
            {tab === "Today" && (
              <>
                <div className="cc-metrics">
                  <Metric label="Domains observed" value={discoverySummary.domainsObserved} />
                  <Metric label="IP ranges observed" value={discoverySummary.ipRangesObserved} />
                  <Metric label="Public services observed" value={discoverySummary.publicServicesObserved} />
                  <Metric label="Technologies observed" value={discoverySummary.technologiesObserved} />
                  <Metric label="Security observations" value={discoverySummary.securityObservations} />
                  <Metric label="Coverage" value={discoverySummary.coverage.available ? `${discoverySummary.coverage.value.observed}/${discoverySummary.coverage.value.checks}` : "—"} sub={discoverySummary.coverage.available ? "checks observed" : "not reported"} />
                </div>
                <p className="cc-lastobs">Public surface of {discoverySummary.domain} · Last observed {fmt(discoverySummary.observedAt)} · external domain (not your environment)</p>
                <h3 className="cc-h3">Atlas already looked — here is what deserves attention</h3>
                {rankedIntel.length === 0 ? <p className="cc-muted">No public-posture observations surfaced for this target.</p> : (
                  <div className="cc-cards">{rankedIntel.map((c) => <DiscoveryIntelCard key={c.id} c={c} onInvestigate={investigateIntel} onEvidence={openIntelEvidence} onRelationships={() => setTab("Relationships")} />)}</div>
                )}
              </>
            )}

            {tab === "Environment" && (
              <div className="cc-env">{Object.entries(discoveryProjection.dataset.entities.reduce<Record<string, string[]>>((g, e) => { (g[e.type] ??= []).push(e.displayName ?? e.name); return g; }, {})).map(([type, names]) => (
                <div key={type} className="cc-env__group"><h3 className="cc-h3">{type} <span className="cc-count">{names.length}</span></h3><div className="cc-rows">{names.map((n) => <div key={n} className="cc-row"><span className="cc-row__name">{n}</span></div>)}</div></div>
              ))}</div>
            )}

            {tab === "Relationships" && (
              discoveryProjection.dataset.relationships.length > 0 ? (
                <>
                  <p className="cc-lead">Observed footprint associations for {discoverySummary.domain}. These are <strong>owns / exposes / uses</strong> associations Atlas observed — <em>not</em> dependency, network reachability, application ownership, or attack paths.</p>
                  <ul className="cc-edges">{discoveryProjection.dataset.relationships.map((r) => <li key={r.id} className="cc-edge"><span className="cc-edge__type">{r.type}</span> {r.from} → {r.to}</li>)}</ul>
                </>
              ) : <Unavailable title="No relationships observed" reason="This observation surfaced no cross-entity associations." missing="a richer discovery pass or an assessment graph over this target" />
            )}

            {tab === "Investigate" && (
              !selectedIntel ? <p className="cc-muted">Select an intelligence card (from Today) to investigate.</p> : (
                <div className="cc-investigate">
                  <div className="cc-card__row"><span className={`cc-chip cc-chip--${selectedIntel.attention ? "attention" : selectedIntel.kind}`}>{selectedIntel.attention ? "Attention" : selectedIntel.kind}</span></div>
                  <h3 className="cc-h3">{selectedIntel.title}</h3>
                  <p className="cc-lead">{selectedIntel.detail}</p>
                  <div className="cc-two">
                    <div>
                      <h4 className="cc-h4">Context</h4>
                      <p className="cc-muted">basis: {selectedIntel.basis}</p>
                      <h4 className="cc-h4">Relationships</h4>
                      <ul className="cc-edges">{selectedIntel.relatedEntityIds.map((id) => <li key={id} className="cc-edge"><span className="cc-edge__type">concerns</span> {id}</li>)}</ul>
                    </div>
                    <div>
                      <h4 className="cc-h4">Evidence</h4>
                      <button className="cc-btn cc-btn--ghost" onClick={() => openIntelEvidence(selectedIntel)}>View evidence ({selectedIntel.evidence.length})</button>
                    </div>
                  </div>
                  <Unavailable title="Timeline · replay · impact" reason="These require change history and an internal assessment. A discovery brief is a single public-surface observation, so timeline/replay/impact cannot yet be derived for this target." missing="≥2 observations (@atlas/memory) + an authorized assessment (@atlas/impact)" />
                </div>
              )
            )}

            {tab === "Executive" && (
              <div className="cc-exec-full">
                <h3 className="cc-h3">Executive interpretation — {discoveryExec.domain}</h3>
                <h4 className="cc-h4">What Atlas observed</h4>
                {discoveryExec.whatObserved.length ? <ul className="cc-edges">{discoveryExec.whatObserved.map((t, i) => <li key={i} className="cc-edge">{t}</li>)}</ul> : <p className="cc-muted">No public footprint observed.</p>}
                <h4 className="cc-h4">What deserves attention</h4>
                {discoveryExec.whatDeservesAttention.length ? <ul className="cc-edges">{discoveryExec.whatDeservesAttention.map((r, i) => <li key={i} className="cc-edge"><span className="cc-edge__type">attention</span> {r.text} <span className="cc-row__ev">· {r.basis}</span></li>)}</ul> : <p className="cc-muted">No public-posture risks flagged by Atlas for this target.</p>}
                <h4 className="cc-h4">What is verified</h4>
                {discoveryExec.whatIsVerified.length ? <ul className="cc-edges">{discoveryExec.whatIsVerified.map((t, i) => <li key={i} className="cc-edge"><span className="cc-edge__type">verified</span> {t}</li>)}</ul> : <p className="cc-muted">Nothing independently verified in this observation.</p>}
                <h4 className="cc-h4">What Atlas cannot yet determine</h4>
                <ul className="cc-edges">{discoveryExec.whatCannotYetDetermine.map((t, i) => <li key={i} className="cc-edge cc-edge--muted">{t}</li>)}</ul>
                <div className="cc-note"><strong>Recommended next step.</strong> {discoveryExec.recommendedNextStep}</div>
              </div>
            )}

            {tab === "Findings" && (
              <Unavailable title="Severity-ranked findings require an assessment run" reason="This is a discovery observation of the public surface. Atlas surfaces public posture on Today and Executive, but severity-scored findings, controls and risk scoring come from an authorized assessment — they are never invented from a discovery brief." missing="@atlas/assessment run over this target" />
            )}

            {tab === "Changes" && (
              <Unavailable title="Change history not yet available" reason="Only one observation of this target exists. Change history requires at least two snapshots to compare — this is 'not yet available', not '0 changes'." missing="a second Atlas observation of this target to diff against" />
            )}

            {tab === "Assessments" && (
              <>
                <Unavailable title={`No persisted assessment for ${discoverySummary.domain}`} reason="This target has a discovery observation but no authorized assessment engagement on record. Atlas will not fabricate an assessment from discovery data." missing="an authorized @atlas/assessment run over this target" />
                <div className="cc-note" style={{ marginTop: 16 }}><strong>Legitimate next step.</strong> An assessment is an authorized, scoped engagement that proves internal posture and quantifies impact against real inventory — distinct from this public-surface observation. Start it from the assessment runner; it is not derived from discovery data.</div>
              </>
            )}
          </section>
        )}
      </main>

      {drawer?.kind === "assessment" && <AssessmentEvidenceDrawer title={drawer.title} evidence={drawer.evidence} onClose={() => setDrawer(null)} />}
      {drawer?.kind === "discovery" && <DiscoveryEvidenceDrawer title={drawer.title} evidence={drawer.evidence} onClose={() => setDrawer(null)} />}
    </div>
  );
}
